import { Box, Typography, useTheme } from "@mui/material";
import { match } from "ts-pattern";

import { availabilityGridFills } from "./AvailabilityGrid.js";

export type AvailabilityGridLegendSwatch = "available" | "unavailable" | "past";

export interface AvailabilityGridLegendProps {
  /** Which states to explain, in the order they are shown. */
  swatches: readonly AvailabilityGridLegendSwatch[];
  "data-testid"?: string;
}

/** Legend for a grid's cell states, filled from {@link availabilityGridFills}. */
export function AvailabilityGridLegend({
  swatches,
  "data-testid": dataTestId = "availability-grid-legend",
}: AvailabilityGridLegendProps) {
  const theme = useTheme();
  const fills = availabilityGridFills(theme);

  return (
    <Box
      data-testid={dataTestId}
      sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2 }}
    >
      {swatches.map((swatch) => {
        const { label, fill } = match(swatch)
          .with("available", () => ({
            label: "Available",
            fill: fills.available,
          }))
          .with("unavailable", () => ({
            label: "Unavailable",
            fill: "transparent",
          }))
          .with("past", () => ({
            label: "Past",
            fill: "transparent",
          }))
          .exhaustive();
        const slashed = swatch === "past";

        return (
          <Box
            key={swatch}
            data-testid={`${dataTestId}-${swatch}`}
            sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
          >
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: 0.5,
                border: `1px solid ${theme.palette.common.lightGray}`,
                backgroundColor: fill,
                backgroundImage: slashed ? fills.pastSlash : "none",
                backgroundBlendMode: "multiply",
              }}
            />
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}
